import { useMemo } from 'react';
import { ComparePlanDTO, useComparePlansQuery } from '../../api/useComparePlansQuery';
import { useComparePlansDisplayOrderQuery } from '../../api/useComparePlansDisplayOrderQuery';
import { AsyncComponentHookResultBase } from '../../../../../../../../Types/ComponentHookResult';
import { PLANS_MIN_COUNT } from '../../RetirementSideBySide.meta';
import {
  mapPlansByOrderedIds,
  orderPlanIdsByDisplayOrder,
} from '../../utils/orderPlanIdsByDisplayOrder';

interface UseCompareTableArgs {
  clientId: string;
  planIds: string[];
}

export interface ComparePlan extends ComparePlanDTO {
  socialSecurityFilingAge: string | null;
  socialSecurityFilingAgeSpouse: string | null;
}

interface UseCompareTableResult extends AsyncComponentHookResultBase<ComparePlan[]> {
  orderedPlanIds: string[];
}

export const useCompareTable = ({
  clientId,
  planIds,
}: UseCompareTableArgs): UseCompareTableResult => {
  const queryInfo = useComparePlansQuery({ clientId, planIds });
  const { data: displayOrder } = useComparePlansDisplayOrderQuery({
    clientId,
    enabled: planIds.length >= PLANS_MIN_COUNT,
  });

  const orderedPlanIds = useMemo(
    () => orderPlanIdsByDisplayOrder(planIds, displayOrder),
    [displayOrder, planIds],
  );

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const mappedPlans = queryInfo.data.map(plan => {
      return {
        ...plan,
        socialSecurityFilingAge: plan.socialSecurityFilingAgeYear
          ? `${plan.socialSecurityFilingAgeYear} ${
              plan.socialSecurityFilingAgeMonth && plan.socialSecurityFilingAgeMonth > 0
                ? `& ${plan.socialSecurityFilingAgeMonth} months`
                : ''
            }`
          : null,
        socialSecurityFilingAgeSpouse: plan.socialSecurityFilingAgeYearSpouse
          ? `${plan.socialSecurityFilingAgeYearSpouse} ${
              plan.socialSecurityFilingAgeMonthSpouse && plan.socialSecurityFilingAgeMonthSpouse > 0
                ? `& ${plan.socialSecurityFilingAgeMonthSpouse} months`
                : ''
            }`
          : null,
      };
    });

    return mapPlansByOrderedIds(mappedPlans, orderedPlanIds);
  }, [orderedPlanIds, queryInfo.data]);

  return {
    ...queryInfo,
    data,
    orderedPlanIds,
  };
};
